/**
 * SECCIÓN 2 — Frontend PSC (Investigaciones HSEQ).
 * MVP funcional: wrapper + listado + detalle con 8 tabs operativas.
 * Sigue los design tokens y componentes UI del proyecto existente.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import SuperAdminModuleView from "../../components/SuperAdminModuleView";
import { Modal } from "../../components/ui";
import {
  Plus, FolderOpen, Download, Upload, AlertTriangle, Trash2, FileText,
  Sparkles, Loader2, CheckCircle, Eye, Activity, Trophy, Copy, Power,
  Globe, Building, X, Edit2, Settings,
} from "lucide-react";
import {
  METHODOLOGIES, methodologiesFor, ALL_STANDARD_METHODS, METHODOLOGY_DESCRIPTIONS,
} from "./constants/methodologies";
import { METHODOLOGY_STEPS, slugForMethodology } from "./constants/methodology_steps";
import MethodologyDetailPanel from "./components/MethodologyDetailPanel";
import MethodologyBackgroundReports from "./components/MethodologyBackgroundReports";
import CrossValEnriched from "./components/CrossValEnriched";
import ScenarioTab from "./components/ScenarioTab";

const RISK_COLORS = { low: "#00C853", medium: "#FFD600", high: "#FF6D00", catastrophic: "#D50000" };
const STATUS_LABELS = {
  Open: "Abierto", "Under Investigation": "En Investigación",
  "RCA in Progress": "RCA en Proceso", "Action Plan": "Plan de Acción",
  Monitoring: "Monitoreo", Closed: "Cerrado",
};

function Badge({ children, color = "#1565C0" }) {
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}55`,
      padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function VisibilityIcon({ v }) {
  if (v === "shared") return <span title="Compartido">👥</span>;
  if (v === "client") return <span title="Visible para todo el cliente">🏢</span>;
  return <span title="Privado">🔒</span>;
}

// ─────────────────────────────────────────────────────────────────
// useInvestigations hook
// ─────────────────────────────────────────────────────────────────
function useInvestigations() {
  const fetchCases = useCallback((filters = {}) => api.get("/investigations", { params: filters }), []);
  const fetchCase = useCallback((id) => api.get(`/investigations/${id}`), []);
  const createCase = useCallback((data) => api.post("/investigations", data), []);
  const updateCase = useCallback((id, data) => api.put(`/investigations/${id}`, data), []);
  const deleteCase = useCallback((id) => api.delete(`/investigations/${id}`), []);
  const loadDemo = useCallback(() => api.get("/investigations/demo-case"), []);
  const uploadFiles = useCallback((id, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return api.post(`/investigations/${id}/evidence/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  }, []);
  const deleteFile = useCallback((id, fid) => api.delete(`/investigations/${id}/evidence/files/${fid}`), []);
  const extract = useCallback((id) => api.post(`/investigations/${id}/evidence/extract`), []);
  const analyze = useCallback((id) => api.post(`/investigations/${id}/evidence/analyze`), []);
  const validateAnalysis = useCallback((id, notes) => api.post(`/investigations/${id}/evidence/validate`, { notes }), []);
  const runRCA = useCallback((id, methodologies) => api.post(`/investigations/${id}/run-rca`, { methodologies }), []);
  const runCrossVal = useCallback((id, methodologies) => api.post(`/investigations/${id}/run-crossval`, { methodologies }), []);
  const runTRIZ = useCallback((id) => api.post(`/investigations/${id}/run-triz`), []);
  const genPlan = useCallback((id) => api.post(`/investigations/${id}/generate-action-plan`), []);
  const runMSIA = useCallback((id, data) => api.post(`/investigations/${id}/run-msia-comms`, data), []);
  const fetchTrends = useCallback(() => api.get("/investigations/trends/summary"), []);
  async function downloadBlob(path, filename) {
    const res = await api.post(path, {}, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }
  const generateReport = useCallback((id, fmt) => downloadBlob(`/investigations/${id}/report/${fmt}`, `${id}.${fmt}`), []);
  const generateEvidenceReport = useCallback((id, fmt) => downloadBlob(`/investigations/${id}/evidence/report/${fmt}`, `${id}_evidence.${fmt}`), []);
  return {
    fetchCases, fetchCase, createCase, updateCase, deleteCase, loadDemo,
    uploadFiles, deleteFile, extract, analyze, validateAnalysis,
    runRCA, runCrossVal, runTRIZ, genPlan, runMSIA, fetchTrends,
    generateReport, generateEvidenceReport,
  };
}

// ─────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────
function Dashboard() {
  const inv = useInvestigations();
  const [items, setItems] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [demo, setDemo] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Safety/HSE", riskLevel: "medium", location: "" });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([inv.fetchCases({ limit: 100 }), inv.fetchTrends()]);
      setItems(r1.data.items || []);
      setTrends(r2.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const kpis = useMemo(() => {
    const open = items.filter((i) => i.status === "Open").length;
    const inProg = items.filter((i) => ["Under Investigation", "RCA in Progress", "Action Plan", "Monitoring"].includes(i.status)).length;
    const crit = items.filter((i) => ["high", "catastrophic"].includes(i.riskLevel)).length;
    const closed = items.filter((i) => i.status === "Closed").length;
    return { open, inProg, crit, closed };
  }, [items]);

  async function submitNew() {
    if (!form.title) return;
    setCreating(true);
    try {
      const r = await inv.createCase(form);
      setShowNew(false);
      setForm({ title: "", description: "", category: "Safety/HSE", riskLevel: "medium", location: "" });
      setSelected(r.data);
      await reload();
    } catch (e) {
      alert("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setCreating(false); }
  }

  async function openDemo() {
    const r = await inv.loadDemo();
    setDemo(r.data);
  }

  async function removeCase(id) {
    if (!confirm("¿Eliminar el caso " + id + "?")) return;
    try { await inv.deleteCase(id); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
  }

  if (loading) return <div className="card text-dim" data-testid="psc-loading">Cargando investigaciones…</div>;

  if (selected) return <CaseDetail caseId={selected.id} onBack={() => { setSelected(null); reload(); }} />;
  if (demo) return <CaseDetail demoCase={demo} onBack={() => setDemo(null)} />;

  return (
    <div data-testid="psc-dashboard" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold">Investigaciones HSEQ</h1>
        <div className="flex gap-2">
          <button className="btn" onClick={openDemo} data-testid="psc-demo-btn">
            <FolderOpen size={14}/> Caso Demo
          </button>
          <button className="btn btn-primary" onClick={() => setShowNew(true)} data-testid="psc-new-btn">
            <Plus size={14}/> Nuevo Caso
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="kpi-card" data-testid="psc-kpi-open"><div className="label">Abiertos</div><div className="value">{kpis.open}</div></div>
        <div className="kpi-card" data-testid="psc-kpi-inprog"><div className="label">En Proceso</div><div className="value">{kpis.inProg}</div></div>
        <div className="kpi-card" data-testid="psc-kpi-crit"><div className="label" style={{ color: "#D50000" }}>Riesgo Crítico</div><div className="value">{kpis.crit}</div></div>
        <div className="kpi-card" data-testid="psc-kpi-closed"><div className="label">Cerrados</div><div className="value">{kpis.closed}</div></div>
      </div>

      <div className="card">
        <div className="card-title">Casos del cliente</div>
        <div className="table-wrap" style={{ overflowX: "auto", marginTop: 8 }}>
          <table className="table" data-testid="psc-cases-table">
            <thead>
              <tr><th>ID</th><th>Título</th><th>Riesgo</th><th>Estado</th><th>Creado por</th><th>Vis.</th><th>Fecha</th><th>Acciones</th></tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="text-center text-dim">Sin casos. Crea tu primer caso o explora el caso Demo.</td></tr>}
              {items.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`psc-row-${c.id}`}
                  onClick={() => setSelected({ id: c.id })}
                  style={{ cursor: "pointer" }}
                  title="Clic para abrir el caso"
                >
                  <td><b>{c.id}</b></td>
                  <td>{c.title}</td>
                  <td><Badge color={RISK_COLORS[c.riskLevel] || "#1565C0"}>{c.riskLevel}</Badge></td>
                  <td><Badge>{STATUS_LABELS[c.status] || c.status}</Badge></td>
                  <td className="text-xs">{c.owner_name || c.owner_id}</td>
                  <td><VisibilityIcon v={c.visibility} /></td>
                  <td className="text-xs">{c.createdAt ? new Date(c.createdAt).toLocaleDateString("es-MX") : "-"}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button className="btn btn-sm" onClick={() => setSelected({ id: c.id })} data-testid={`psc-open-${c.id}`}><Eye size={12}/></button>
                      <button className="btn btn-sm" onClick={() => removeCase(c.id)} data-testid={`psc-del-${c.id}`} title="Eliminar"><Trash2 size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {trends && trends.leaderboard && trends.leaderboard.length > 0 && (
        <div className="card" data-testid="psc-leaderboard">
          <div className="card-title flex items-center gap-2"><Trophy size={16}/> Líderes de Investigación</div>
          <div className="text-xs text-dim mb-2">Solo visible en tu organización</div>
          {trends.leaderboard.slice(0, 5).map((u, i) => (
            <div key={u.owner_id} className="flex items-center justify-between" style={{ padding: "6px 0", borderBottom: "1px solid var(--border-2)" }}>
              <div>
                <span className="mr-2">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i+1}`}</span>
                <b>{u.owner_name}</b> &nbsp;<Badge>{u.nivel_experto}</Badge>
              </div>
              <div className="text-xs text-dim">
                {u.casos_liderados} casos · {u.metodologias_usadas} métodos · {u.barreras_documentadas} barreras · <b>{u.puntuacion_total} pts</b>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={showNew} onClose={() => setShowNew(false)} title="Nuevo Caso de Investigación" size="md" testId="psc-new-modal">
        <div className="space-y-2">
          <div><label className="label">Título *</label><input className="input" data-testid="psc-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}/></div>
          <div><label className="label">Descripción</label><textarea className="input" rows={3} data-testid="psc-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Categoría</label>
              <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {["Safety/HSE", "Environmental", "Process Safety", "Quality", "Asset Integrity", "Human Factors", "Security"].map((c) => <option key={c}>{c}</option>)}
              </select></div>
            <div><label className="label">Nivel de Riesgo</label>
              <select className="input" data-testid="psc-form-risk" value={form.riskLevel} onChange={(e) => setForm({ ...form, riskLevel: e.target.value })}>
                {["low", "medium", "high", "catastrophic"].map((r) => <option key={r}>{r}</option>)}
              </select></div>
          </div>
          <div><label className="label">Ubicación</label><input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}/></div>
          <div className="flex justify-end gap-2 mt-2">
            <button className="btn" onClick={() => setShowNew(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={submitNew} disabled={!form.title || creating} data-testid="psc-form-save">
              {creating ? <Loader2 size={14} className="spin"/> : <Plus size={14}/>} Crear
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CaseDetail con 8 tabs operativas
// ─────────────────────────────────────────────────────────────────
const TABS = [
  { key: "overview",    label: "📋 Resumen" },
  { key: "evidence",    label: "📎 Evidencias" },
  { key: "scenario",    label: "🕒 Escenario y Línea de Tiempo" },
  { key: "investigation", label: "🔍 Investigación IA" },
  { key: "triz",        label: "💡 TRIZ" },
  { key: "plan",        label: "📋 Plan de Acción" },
  { key: "effective",   label: "📊 Efectividad" },
  { key: "culture",     label: "🌱 Cultura" },
  { key: "msia",        label: "🏛 Impacto Sistema" },
  { key: "methodologies", label: "⚙ Metodologías" },
];

function CaseDetail({ caseId, demoCase, onBack }) {
  const inv = useInvestigations();
  const [c, setC] = useState(demoCase || null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(!demoCase);
  const [busy, setBusy] = useState("");
  // Pasar hipótesis seleccionadas del escenario al tab de investigación
  const [selectedHypotheses, setSelectedHypotheses] = useState([]);
  const onNavigateTab = useCallback((key, ctx) => {
    if (ctx?.selectedHypotheses) setSelectedHypotheses(ctx.selectedHypotheses);
    setTab(key);
  }, []);

  const reload = useCallback(async () => {
    if (demoCase) return;
    setLoading(true);
    try { const r = await inv.fetchCase(caseId); setC(r.data); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, demoCase]);

  useEffect(() => { if (!demoCase) reload(); }, [reload, demoCase]);

  async function withBusy(label, fn) {
    setBusy(label);
    try { await fn(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  }

  if (loading || !c) return <div className="card text-dim">Cargando caso…</div>;

  return (
    <div data-testid="psc-case-detail" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <button className="btn btn-sm mr-2" onClick={onBack} data-testid="psc-back">← Volver</button>
          <span className="text-sm text-dim">{c.id}</span>
          <h2 className="text-xl font-bold">{c.title}</h2>
          <div className="flex gap-2 mt-1">
            <Badge color={RISK_COLORS[c.riskLevel]}>{c.riskLevel}</Badge>
            <Badge>{STATUS_LABELS[c.status] || c.status}</Badge>
            <Badge color="#6c757d">{c.category}</Badge>
          </div>
        </div>
        {!demoCase && (
          <div className="flex gap-2">
            <button className="btn btn-sm" onClick={() => inv.generateReport(c.id, "pdf")} data-testid="psc-rep-pdf"><Download size={12}/> PDF</button>
            <button className="btn btn-sm" onClick={() => inv.generateReport(c.id, "xlsx")} data-testid="psc-rep-xlsx"><Download size={12}/> Excel</button>
            <button className="btn btn-sm" onClick={() => inv.generateReport(c.id, "docx")} data-testid="psc-rep-docx"><Download size={12}/> Word</button>
            <button className="btn btn-sm" onClick={() => inv.generateReport(c.id, "pptx")} data-testid="psc-rep-pptx"><Download size={12}/> PPT</button>
          </div>
        )}
      </div>

      <div className="flex gap-1 flex-wrap" style={{ borderBottom: "1px solid var(--border-2)" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={"tab" + (tab === t.key ? " active" : "")}
            style={{
              padding: "6px 12px", border: 0,
              borderBottom: tab === t.key ? "2px solid var(--accent)" : "2px solid transparent",
              background: "transparent", color: tab === t.key ? "var(--text)" : "var(--text-dim)",
              cursor: "pointer", fontSize: 12,
            }}
            onClick={() => setTab(t.key)}
            data-testid={`psc-tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "overview"      && <OverviewTab c={c} demoMode={!!demoCase} inv={inv}/>}
      {tab === "evidence"      && <EvidenceTab c={c} demoMode={!!demoCase} inv={inv} reload={reload} busy={busy} withBusy={withBusy}/>}
      {tab === "scenario"      && <ScenarioTab c={c} reload={reload} onNavigate={onNavigateTab}/>}
      {tab === "investigation" && <InvestigationTab c={c} demoMode={!!demoCase} inv={inv} reload={reload} busy={busy} withBusy={withBusy} initialHypotheses={selectedHypotheses}/>}
      {tab === "triz"          && <TrizTab c={c} demoMode={!!demoCase} inv={inv} reload={reload} busy={busy} withBusy={withBusy}/>}
      {tab === "plan"          && <PlanTab c={c} demoMode={!!demoCase} inv={inv} reload={reload} busy={busy} withBusy={withBusy}/>}
      {tab === "effective"     && <EffectivenessTab c={c} demoMode={!!demoCase} inv={inv} reload={reload}/>}
      {tab === "culture"       && <CultureTab c={c} demoMode={!!demoCase} inv={inv} reload={reload}/>}
      {tab === "msia"          && <MsiaTab c={c} demoMode={!!demoCase} inv={inv} reload={reload} busy={busy} withBusy={withBusy}/>}
      {tab === "methodologies" && <MethodologiesTab demoMode={!!demoCase}/>}
    </div>
  );
}

function OverviewTab({ c }) {
  const ar = c.evidence?.analysis_result;
  return (
    <div className="card space-y-2">
      <div><b>Descripción:</b> {c.description || "—"}</div>
      <div><b>Ubicación:</b> {c.location || "—"} · <b>Fecha:</b> {c.date || "—"}</div>
      <div><b>Owner:</b> {c.owner_name || c.owner_id || "—"}</div>
      {ar && ar.executive_summary && (
        <div className="card" style={{ background: "rgba(0,200,83,0.08)", borderColor: "#00C853" }}>
          <b>✓ Análisis de evidencias disponible</b>
          <div className="text-sm mt-1">{ar.executive_summary}</div>
        </div>
      )}
    </div>
  );
}

function EvidenceTab({ c, demoMode, inv, reload, busy, withBusy }) {
  const [files, setFiles] = useState([]);
  const fileInputRef = useState(null)[0];
  async function onUpload() {
    if (!files.length) return;
    // Validación frontend Bloque 1 — 50 MB por archivo
    const MAX = 50 * 1024 * 1024;
    const oversize = files.find((f) => f.size > MAX);
    if (oversize) {
      alert(`El archivo ${oversize.name} supera el límite de 50 MB. Comprime el video o usa un enlace de Google Drive.`);
      return;
    }
    await withBusy("upload", async () => {
      await inv.uploadFiles(c.id, files);
      setFiles([]); await reload();
      // Bloque 2 — Extracción automática tras upload (sin botón manual).
      try {
        await inv.extract(c.id);
        await reload();
      } catch (e) {
        console.warn("Auto-extracción falló (no bloqueante):", e?.message);
      }
    });
  }
  const ex = c.evidence?.extraction_result;
  const an = c.evidence?.analysis_result;
  const validated = !!c.evidence?.analysis_validated_by;
  return (
    <div className="space-y-3">
      {!demoMode && (
        <div className="card">
          <div className="card-title">1. Archivos de evidencia</div>
          <input type="file" multiple onChange={(e) => setFiles([...e.target.files])} data-testid="psc-file-input" accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx,.txt,.mp4,.mov"/>
          <div className="text-xs text-dim mt-1">
            PDF, DOCX, JPG, PNG, MP4 — máximo 50 MB por archivo.
            La extracción se ejecuta automáticamente al subir.
          </div>
          <div className="mt-2 flex gap-2">
            <button className="btn btn-primary" disabled={!files.length || busy === "upload"} onClick={onUpload} data-testid="psc-upload-btn">
              {busy === "upload" ? <Loader2 size={12} className="spin"/> : <Upload size={12}/>} Subir {files.length} archivo(s)
            </button>
          </div>
          <ul className="mt-2 text-xs">
            {(c.evidence?.files || []).map((f) => (
              <li key={f.file_id} className="flex justify-between" style={{ padding: "4px 0", borderBottom: "1px solid var(--border-2)" }}>
                <span>{f.original_name} <span className="text-dim">({f.size_kb} KB)</span></span>
                <button className="btn btn-sm" onClick={async () => { await inv.deleteFile(c.id, f.file_id); await reload(); }}><Trash2 size={10}/></button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="card">
        <div className="card-title flex items-center justify-between">
          <span>2. Extracción IA</span>
          <span className="text-xs text-dim">
            {ex ? "✓ Completada automáticamente" : (c.evidence?.files || []).length ? "⏳ Procesando..." : "Sin archivos aún"}
          </span>
        </div>
        {ex && (ex.extracted_facts || ex.equipment_involved) ? (
          <div className="mt-2 text-sm">
            <div className="grid grid-cols-4 gap-2 mb-2">
              <div className="kpi-card"><div className="label">Hechos</div><div className="value">{(ex.extracted_facts || []).length}</div></div>
              <div className="kpi-card"><div className="label">Fechas</div><div className="value">{(ex.key_dates || []).length}</div></div>
              <div className="kpi-card"><div className="label">Equipos</div><div className="value">{(ex.equipment_involved || []).length}</div></div>
              <div className="kpi-card"><div className="label">Personas</div><div className="value">{(ex.personnel_involved || []).length}</div></div>
            </div>
            <details><summary className="cursor-pointer">Ver hechos extraídos ({(ex.extracted_facts || []).length})</summary>
              <ul className="text-xs mt-1">
                {(ex.extracted_facts || []).slice(0, 30).map((f, i) => (
                  <li key={i}>[{f.category}] {f.fact} — <i>{f.confidence}</i></li>
                ))}
              </ul>
            </details>
          </div>
        ) : <div className="text-dim text-sm mt-1">Sube archivos y ejecuta la extracción.</div>}
      </div>
      <div className="card">
        <div className="card-title flex items-center justify-between">3. Análisis IA
          <div className="flex gap-2">
            {!demoMode && <button className="btn btn-sm" disabled={busy === "analyze" || !ex} onClick={() => withBusy("analyze", async () => { await inv.analyze(c.id); await reload(); })} data-testid="psc-analyze-btn">
              {busy === "analyze" ? <Loader2 size={12} className="spin"/> : <Activity size={12}/>} Ejecutar
            </button>}
            {an && !demoMode && <button className="btn btn-sm" onClick={() => inv.generateEvidenceReport(c.id, "pdf")}><Download size={12}/> PDF</button>}
            {an && !demoMode && <button className="btn btn-sm" onClick={() => inv.generateEvidenceReport(c.id, "docx")}><Download size={12}/> Word</button>}
          </div>
        </div>
        {an ? (
          <div className="mt-2 space-y-2 text-sm">
            {!validated && !demoMode && (
              <div className="card" style={{ background: "rgba(255,214,0,0.10)", borderColor: "#FFD600" }}>
                <b>BORRADOR — Pendiente de validación</b>
                <button className="btn btn-sm btn-primary ml-2" onClick={() => withBusy("validate", async () => { await inv.validateAnalysis(c.id, ""); await reload(); })} data-testid="psc-validate-btn">
                  {busy === "validate" ? <Loader2 size={12} className="spin"/> : <CheckCircle size={12}/>} Validar Análisis
                </button>
              </div>
            )}
            {validated && <div className="text-xs" style={{ color: "#00C853" }}>✓ Validado por {c.evidence.analysis_validated_by} · {c.evidence.analysis_validated_at}</div>}
            {an.executive_summary && <div className="card"><b>Resumen Ejecutivo</b><div>{an.executive_summary}</div></div>}
            {(an.preliminary_causal_factors || []).length > 0 && <div><b>Factores Causales Preliminares:</b><ul>{an.preliminary_causal_factors.map((f, i) => <li key={i}>{f}</li>)}</ul></div>}
            <div className="text-xs text-dim italic">Documento de pre-investigación generado por IA. Requiere validación antes de su uso.</div>
          </div>
        ) : <div className="text-dim text-sm mt-1">Requiere extracción previa.</div>}
      </div>
    </div>
  );
}

function InvestigationTab({ c, demoMode, inv, reload, busy, withBusy, initialHypotheses }) {
  // PARTE 4 — CrossVal sin restricciones por riesgo. Mostramos TODAS las metodologías estándar.
  const standardMethods = ALL_STANDARD_METHODS;
  const [customMethods, setCustomMethods] = useState([]);
  const [selected, setSelected] = useState(c.investigation?.methodologies || []);
  const [toast, setToast] = useState("");
  const [copied, setCopied] = useState(false);

  // Hipótesis seleccionadas en la pestaña Escenario (Bloque 4)
  const scenarioHypotheses = (c.investigation?.scenario?.hypotheses || []).filter(
    (h) => (initialHypotheses || []).includes(h.id || h.title)
  );

  useEffect(() => {
    api.get("/methodologies").then((r) => setCustomMethods(r.data || [])).catch(() => {});
  }, []);

  function toggle(m, disabled) {
    if (disabled) return;
    if (selected.includes(m)) {
      setSelected(selected.filter((x) => x !== m));
      return;
    }
    if (selected.length >= 5) {
      setToast("Máximo 5 metodologías en Validación Cruzada.");
      setTimeout(() => setToast(""), 2500);
      return;
    }
    setSelected([...selected, m]);
  }

  function copyArg() {
    const text = c.investigation?.crossValResult?.persuasionArgument || "";
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  const ai = c.investigation?.aiResults;
  const cv = c.investigation?.crossValResult;
  const N = selected.length;
  const activeCustom = customMethods.filter((m) => m.status === "active");
  const processingCustom = customMethods.filter((m) => m.status === "processing");

  return (
    <div className="space-y-3" data-testid="psc-investigation-tab">
      <div className="card">
        <div className="card-title">Metodologías Estándar</div>
        <div className="text-xs text-dim mb-1">
          Selecciona entre 2 y 5 metodologías para comparar.
          Puedes combinar cualquier metodología.
        </div>
        <div className="flex gap-2 flex-wrap mt-1" data-testid="psc-standard-methods">
          {standardMethods.map((m) => {
            const sel = selected.includes(m);
            const disabled = !sel && N >= 5;
            return (
              <button
                key={m}
                onClick={() => toggle(m, disabled)}
                title={METHODOLOGY_DESCRIPTIONS[m] ? `${METHODOLOGY_DESCRIPTIONS[m].origin} (${METHODOLOGY_DESCRIPTIONS[m].year})` : ""}
                className={"btn btn-sm" + (sel ? " btn-primary" : "")}
                style={disabled ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                data-testid={`psc-method-${m.replace(/[^a-z0-9]/gi, "")}`}
              >{m}</button>
            );
          })}
        </div>

        {(activeCustom.length > 0 || processingCustom.length > 0) && (
          <div className="mt-3">
            <div className="card-title" style={{ fontSize: 12 }}>✨ Metodologías Personalizadas</div>
            <div className="flex gap-2 flex-wrap mt-1" data-testid="psc-custom-methods">
              {activeCustom.map((m) => {
                const sel = selected.includes(m.name);
                const disabled = !sel && N >= 5;
                return (
                  <button
                    key={m.id}
                    onClick={() => toggle(m.name, disabled)}
                    title={m.description || ""}
                    className={"btn btn-sm" + (sel ? " btn-primary" : "")}
                    style={disabled ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                    data-testid={`psc-custom-${m.id}`}
                  >
                    {m.name} <span style={{ fontSize: 9, marginLeft: 4, opacity: 0.7 }}>{m.is_global ? "🌐 Global" : "🏢 Custom"}</span>
                  </button>
                );
              })}
              {processingCustom.map((m) => (
                <button
                  key={m.id}
                  disabled
                  title="La IA está procesando esta metodología. Disponible en breve."
                  className="btn btn-sm"
                  style={{ opacity: 0.5, cursor: "wait" }}
                  data-testid={`psc-custom-processing-${m.id}`}
                >
                  ⏳ {m.name} <span style={{ fontSize: 9, marginLeft: 4 }}>En adopción</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-2 flex items-center gap-3 flex-wrap" data-testid="psc-method-counter">
          <span style={{ fontSize: 12, fontWeight: 600 }}>{N} / 5 metodologías seleccionadas</span>
          {N < 2 && <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Selecciona mínimo 2 para CrossVal</span>}
          {N >= 2 && <span style={{ fontSize: 11, color: "#00C853" }}>✓ CrossVal disponible</span>}
        </div>
        {toast && <div className="card mt-2" style={{ background: "rgba(213,0,0,0.10)", borderColor: "#D50000", fontSize: 12 }} data-testid="psc-method-toast">{toast}</div>}

        <div className="mt-2 flex gap-2">
          {!demoMode && <button className="btn btn-primary" disabled={busy === "rca" || N === 0} onClick={() => withBusy("rca", async () => { await inv.runRCA(c.id, selected); await reload(); })} data-testid="psc-rca-btn">
            {busy === "rca" ? <Loader2 size={12} className="spin"/> : <Sparkles size={12}/>} Ejecutar Investigación IA
          </button>}
          {N >= 2 && !demoMode && <button className="btn" disabled={busy === "cv"} onClick={() => withBusy("cv", async () => { await inv.runCrossVal(c.id, selected); await reload(); })} data-testid="psc-cv-btn">
            ⚡ Validación Cruzada ({N} métodos)
          </button>}
        </div>
      </div>

      {/* PARTE 3 — Panel de Detalle Paso a Paso por metodología seleccionada */}
      {selected.length > 0 && (
        <MethodologyBackgroundReports
          caseId={c.id}
          selected={selected}
          hypotheses={scenarioHypotheses}
          reload={reload}
          existingReports={c.investigation?.methodology_reports || {}}
        />
      )}

      {selected.length >= 2 && (
        <CrossValEnriched
          caseId={c.id}
          selected={selected}
          reports={c.investigation?.methodology_reports || {}}
          reload={reload}
        />
      )}

      {selected.length > 0 && (
        <div className="space-y-1" data-testid="psc-detail-panels">
          {selected.map((mName) => {
            const stdSlug = slugForMethodology(mName);
            const custom = customMethods.find((m) => m.name === mName);
            const slug = stdSlug || custom?.slug || (custom ? `custom_${custom.id}` : null);
            if (!slug) return null;
            const existing = c.investigation?.methodology_details?.[slug] || null;
            return (
              <MethodologyDetailPanel
                key={`mdp-${mName}`}
                caseId={c.id}
                methodologyName={mName}
                methodologySlug={slug}
                existingDetail={existing}
              />
            );
          })}
        </div>
      )}

      {ai && (
        <div className="card">
          <div className="card-title">Resultados RCA</div>
          {ai.summary && <p>{ai.summary}</p>}
          {(ai.rootCauses || []).length > 0 && (
            <div className="mt-2">
              <b>Causas Raíz:</b>
              <ul className="text-sm">
                {ai.rootCauses.map((r, i) => <li key={i}>[{r.type}] <b>{r.cause}</b> — <i>{r.confidence}</i></li>)}
              </ul>
            </div>
          )}
          {(ai.fiveWhys || []).length > 0 && (
            <details className="mt-2"><summary>5 Whys</summary>
              <ul className="text-sm">{ai.fiveWhys.map((w, i) => <li key={i}><b>Why #{w.why}:</b> {w.question} → {w.answer}</li>)}</ul>
            </details>
          )}
          {!demoMode && <button className="btn btn-sm mt-2" disabled={busy === "plan" || !ai} onClick={() => withBusy("plan", async () => { await inv.genPlan(c.id); await reload(); })} data-testid="psc-genplan-btn">
            {busy === "plan" ? <Loader2 size={12} className="spin"/> : <Sparkles size={12}/>} Generar Plan de Acción IA
          </button>}
        </div>
      )}

      {cv && (cv.convergenceScore != null || (cv.analyses || []).length > 0) && (
        <div className="space-y-2" data-testid="psc-crossval-results">
          {/* Sección 1 — Análisis por metodología */}
          {(cv.analyses || []).length > 0 && (
            <div className="card">
              <div className="card-title">Análisis por metodología</div>
              <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                {(cv.analyses || []).map((a, i) => (
                  <div key={i} className="card" style={{ background: "var(--bg-2)" }} data-testid={`psc-cv-analysis-${i}`}>
                    <div className="font-semibold mb-1">{a.methodology}</div>
                    {a.theoretical_framework && (
                      <details className="text-xs mb-1"><summary>Marco teórico</summary>
                        <div>{a.theoretical_framework}</div>
                      </details>
                    )}
                    {(a.rootCauses || []).length > 0 && (
                      <div className="text-xs"><b>Causas raíz:</b>
                        <ul style={{ paddingLeft: 14 }}>{a.rootCauses.map((r, j) => <li key={j}>{typeof r === "string" ? r : r.cause}</li>)}</ul>
                      </div>
                    )}
                    {(a.uniqueFindings || []).length > 0 && (
                      <div className="text-xs mt-1">
                        <Badge color="#6A1B9A">Único</Badge>
                        <ul style={{ paddingLeft: 14 }}>{a.uniqueFindings.map((u, j) => <li key={j}>{u}</li>)}</ul>
                      </div>
                    )}
                    {a.keyFindings && <div className="text-xs mt-1"><b>Síntesis:</b> {a.keyFindings}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sección 2 — Matriz de Convergencia */}
          {cv.convergenceMatrix && Object.keys(cv.convergenceMatrix || {}).length >= 2 && (
            <div className="card" data-testid="psc-cv-matrix">
              <div className="card-title">Matriz de Convergencia</div>
              <div style={{ overflowX: "auto" }}>
                <ConvergenceMatrix matrix={cv.convergenceMatrix} />
              </div>
            </div>
          )}

          {/* Sección 3 — Causas por nivel de convergencia */}
          <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            <div className="card" style={{ background: "rgba(0,200,83,0.10)", borderColor: "#00C853" }} data-testid="psc-cv-converging">
              <div className="font-semibold" style={{ color: "#00C853" }}>Convergencia Alta (3+ métodos)</div>
              {(cv.convergingCauses || []).length === 0 ? <div className="text-xs text-dim">—</div> :
                <ul className="text-sm" style={{ paddingLeft: 14 }}>{cv.convergingCauses.map((c, i) => <li key={i}>{c}</li>)}</ul>}
            </div>
            <div className="card" style={{ background: "rgba(255,214,0,0.10)", borderColor: "#FFD600" }} data-testid="psc-cv-partial">
              <div className="font-semibold" style={{ color: "#B07A00" }}>Convergencia Parcial (2 métodos)</div>
              {(cv.partiallyConvergingCauses || []).length === 0 ? <div className="text-xs text-dim">—</div> :
                <ul className="text-sm" style={{ paddingLeft: 14 }}>{cv.partiallyConvergingCauses.map((c, i) => <li key={i}>{c}</li>)}</ul>}
            </div>
            <div className="card" style={{ background: "rgba(120,120,120,0.10)", borderColor: "var(--border-2)" }} data-testid="psc-cv-diverging">
              <div className="font-semibold">Sin convergencia (1 método)</div>
              {(cv.divergingCauses || []).length === 0 ? <div className="text-xs text-dim">—</div> :
                <ul className="text-sm" style={{ paddingLeft: 14 }}>{cv.divergingCauses.map((c, i) => <li key={i}>{c}</li>)}</ul>}
            </div>
          </div>

          {/* Sección 4 — Veredicto */}
          {cv.convergenceScore != null && (
            <div className="card" data-testid="psc-cv-verdict">
              <div className="flex items-center gap-3">
                <div style={{
                  fontSize: 36, fontWeight: 700,
                  color: cv.convergenceScore >= 80 ? "#00C853" : cv.convergenceScore >= 60 ? "#B07A00" : "#D50000",
                }}>{cv.convergenceScore}%</div>
                <Badge color={cv.convergenceScore >= 80 ? "#00C853" : cv.convergenceScore >= 60 ? "#FFD600" : "#D50000"}>
                  {cv.convergenceScore >= 80 ? "Alta confianza" : cv.convergenceScore >= 60 ? "Confianza media" : "Baja convergencia"}
                </Badge>
              </div>
              {cv.triangulationVerdict && <p className="mt-2 text-sm">{cv.triangulationVerdict}</p>}
              {(cv.recommendedForRCA || []).length > 0 && (
                <div className="mt-2 text-sm"><b>Metodologías recomendadas:</b> {cv.recommendedForRCA.join(", ")}</div>
              )}
            </div>
          )}

          {/* Sección 5 — Argumento */}
          {cv.persuasionArgument && (
            <div className="card" style={{ background: "rgba(21,101,192,0.10)", borderColor: "#1565C0" }} data-testid="psc-cv-argument">
              <div className="flex items-center justify-between mb-1">
                <b style={{ color: "#1565C0" }}>Argumento para el equipo investigador</b>
                <button className="btn btn-sm" onClick={copyArg} data-testid="psc-cv-copy"><Copy size={12}/> {copied ? "Copiado" : "Copiar"}</button>
              </div>
              <p className="text-sm">{cv.persuasionArgument}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConvergenceMatrix({ matrix }) {
  const keys = Object.keys(matrix);
  const all = new Set(keys);
  for (const k of keys) Object.keys(matrix[k] || {}).forEach((kk) => all.add(kk));
  const cols = [...all];
  function cellColor(v) {
    if (v == null) return "var(--bg-3)";
    if (v >= 80) return "rgba(0,200,83,0.30)";
    if (v >= 60) return "rgba(255,214,0,0.30)";
    return "rgba(213,0,0,0.25)";
  }
  return (
    <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
      <thead>
        <tr><th style={{ padding: 4, borderBottom: "1px solid var(--border-2)" }}></th>{cols.map((c) => <th key={c} style={{ padding: 4, borderBottom: "1px solid var(--border-2)" }}>{c}</th>)}</tr>
      </thead>
      <tbody>
        {cols.map((r) => (
          <tr key={r}>
            <th style={{ padding: 4, textAlign: "left", borderRight: "1px solid var(--border-2)" }}>{r}</th>
            {cols.map((c) => {
              const v = r === c ? 100 : (matrix[r] && matrix[r][c]) ?? (matrix[c] && matrix[c][r]);
              return <td key={c} style={{ padding: 6, textAlign: "center", background: cellColor(v), border: "1px solid var(--border-2)" }}>{v != null ? `${v}%` : "—"}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// MEJORA 3D — Panel de Gestión de Metodologías
// ─────────────────────────────────────────────────────────────────
function MethodologiesTab({ demoMode }) {
  const { user } = useAuth();
  const isSuper = (user?.role || "").toUpperCase() === "SUPER_ADMIN";
  const isAdmin = (user?.role || "").toUpperCase() === "ADMIN";
  const canManage = isSuper || isAdmin;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/methodologies"); setList(r.data || []); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Poll for processing methodologies
  useEffect(() => {
    const hasProcessing = list.some((m) => m.status === "processing");
    if (!hasProcessing) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [list, reload]);

  if (!canManage) {
    return <div className="card text-dim" data-testid="psc-methodologies-no-access">Solo administradores pueden gestionar metodologías personalizadas.</div>;
  }

  const globals = list.filter((m) => m.is_global);
  const tenantList = list.filter((m) => !m.is_global);

  async function toggleActive(m) {
    const next = m.status === "active" ? "inactive" : "active";
    try {
      await api.put(`/methodologies/${m.id}`, { status: next });
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function remove(m) {
    if (!window.confirm(`¿Eliminar la metodología "${m.name}"?`)) return;
    try {
      await api.delete(`/methodologies/${m.id}`);
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }

  return (
    <div className="space-y-3" data-testid="psc-methodologies-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Gestión de Metodologías</h3>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="psc-meth-add"><Plus size={12}/> Agregar Metodología</button>
      </div>

      <div className="card">
        <div className="card-title flex items-center gap-2"><Globe size={14}/> Metodologías Globales</div>
        {globals.length === 0 ? <div className="text-dim text-sm">—</div> :
          globals.map((m) => (
            <MethodRow key={m.id} m={m} canEdit={isSuper} onEdit={() => setEditing(m)} onToggle={() => toggleActive(m)} onDelete={() => remove(m)} />
          ))}
      </div>

      <div className="card">
        <div className="card-title flex items-center gap-2"><Building size={14}/> Metodologías de Mi Organización</div>
        {tenantList.length === 0 ? <div className="text-dim text-sm">—</div> :
          tenantList.map((m) => (
            <MethodRow key={m.id} m={m} canEdit={canManage} onEdit={() => setEditing(m)} onToggle={() => toggleActive(m)} onDelete={() => remove(m)} />
          ))}
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Agregar Metodología" size="lg" testId="psc-meth-modal-add">
        <AddMethodForm isSuper={isSuper} onClose={() => setShowAdd(false)} onSaved={async () => { setShowAdd(false); await reload(); }} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Editar — ${editing?.name || ""}`} size="lg" testId="psc-meth-modal-edit">
        {editing && (
          <EditMethodForm m={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await reload(); }} />
        )}
      </Modal>
    </div>
  );
}

function MethodRow({ m, canEdit, onEdit, onToggle, onDelete }) {
  return (
    <div className="flex items-center justify-between py-2" style={{ borderBottom: "1px solid var(--border-2)" }} data-testid={`psc-meth-row-${m.id}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="flex items-center gap-2 flex-wrap">
          <b>{m.name}</b>
          {m.acronym && <span className="text-dim text-xs">({m.acronym})</span>}
          {m.is_global ? <Badge color="#1565C0">🌐 Global</Badge> : <Badge>🏢 Propia</Badge>}
          <Badge color={m.status === "active" ? "#00C853" : m.status === "processing" ? "#FFD600" : "#9E9E9E"}>
            {m.status === "processing" ? "⏳ En adopción" : m.status === "active" ? "✓ Activa" : "Inactiva"}
          </Badge>
        </div>
        {m.description && <div className="text-xs text-dim mt-1" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.description.slice(0, 140)}</div>}
        <div className="text-xs text-dim">{m.origin || "—"} {m.year ? `· ${m.year}` : ""} · {(m.risk_levels || []).join(", ")}</div>
      </div>
      {canEdit && m.status !== "processing" && (
        <div className="flex gap-1">
          <button className="btn btn-sm" onClick={onEdit} title="Editar" data-testid={`psc-meth-edit-${m.id}`}><Edit2 size={12}/></button>
          <button className="btn btn-sm" onClick={onToggle} title={m.status === "active" ? "Desactivar" : "Activar"} data-testid={`psc-meth-toggle-${m.id}`}><Power size={12}/></button>
          <button className="btn btn-sm" style={{ color: "#D50000" }} onClick={onDelete} title="Eliminar" data-testid={`psc-meth-delete-${m.id}`}><Trash2 size={12}/></button>
        </div>
      )}
    </div>
  );
}

function AddMethodForm({ isSuper, onClose, onSaved }) {
  const [mode, setMode] = useState("manual"); // manual | upload
  const [name, setName] = useState("");
  const [acronym, setAcronym] = useState("");
  const [origin, setOrigin] = useState("");
  const [year, setYear] = useState("");
  const [description, setDescription] = useState("");
  const [purpose, setPurpose] = useState("");
  const [stepsText, setStepsText] = useState("");
  const [keyConceptsText, setKeyConceptsText] = useState("");
  const [riskLevels, setRiskLevels] = useState([]);
  const [isGlobal, setIsGlobal] = useState(false);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  function toggleRisk(r) { setRiskLevels(riskLevels.includes(r) ? riskLevels.filter((x) => x !== r) : [...riskLevels, r]); }

  async function saveManual() {
    if (!name.trim()) { alert("Nombre requerido"); return; }
    setSaving(true);
    try {
      await api.post("/methodologies", {
        name: name.trim(), acronym, origin, year, description, purpose,
        risk_levels: riskLevels,
        steps: stepsText.split("\n").map((s) => s.trim()).filter(Boolean),
        key_concepts: keyConceptsText.split(",").map((s) => s.trim()).filter(Boolean),
        is_global: isGlobal,
      });
      onSaved?.();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  }

  async function uploadFile() {
    if (!name.trim()) { alert("Nombre requerido"); return; }
    if (!file) { alert("Selecciona un archivo PDF o DOCX"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name.trim());
      fd.append("risk_levels", JSON.stringify(riskLevels));
      fd.append("is_global", isGlobal ? "true" : "false");
      const r = await api.post("/methodologies/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProcessingId(r.data?.id);
      // Polling
      const polled = await pollUntilActive(r.data?.id);
      if (polled) onSaved?.();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  }

  async function pollUntilActive(id) {
    for (let i = 0; i < 60; i++) {
      try {
        const r = await api.get(`/methodologies/${id}/status`);
        if (r.data?.status === "active") return true;
      } catch (e) { return false; }
      await new Promise((res) => setTimeout(res, 5000));
    }
    return false;
  }

  return (
    <div className="space-y-3" data-testid="psc-meth-add-form">
      <div className="flex gap-2">
        <button className={`btn btn-sm ${mode === "manual" ? "btn-primary" : ""}`} onClick={() => setMode("manual")} data-testid="psc-meth-mode-manual">Manual</button>
        <button className={`btn btn-sm ${mode === "upload" ? "btn-primary" : ""}`} onClick={() => setMode("upload")} data-testid="psc-meth-mode-upload">Desde Documento</button>
      </div>

      <div><label className="label">Nombre *</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="psc-meth-name"/></div>

      {mode === "manual" ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="label">Siglas/Acrónimo</label><input className="input" value={acronym} onChange={(e) => setAcronym(e.target.value)}/></div>
            <div><label className="label">Organización origen</label><input className="input" value={origin} onChange={(e) => setOrigin(e.target.value)}/></div>
            <div><label className="label">Año</label><input className="input" value={year} onChange={(e) => setYear(e.target.value)}/></div>
          </div>
          <div><label className="label">Descripción *</label><textarea className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}/></div>
          <div><label className="label">Cuándo aplicar</label><textarea className="input" rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)}/></div>
          <div><label className="label">Pasos (uno por línea)</label><textarea className="input" rows={4} value={stepsText} onChange={(e) => setStepsText(e.target.value)}/></div>
          <div><label className="label">Conceptos clave (separar con coma)</label><input className="input" value={keyConceptsText} onChange={(e) => setKeyConceptsText(e.target.value)}/></div>
        </>
      ) : (
        <>
          <div>
            <label className="label">Archivo (PDF o DOCX, máx 20MB) *</label>
            <input className="input" type="file" accept=".pdf,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="psc-meth-file"/>
          </div>
          <div className="text-dim text-xs">Sube el manual o guía. La IA extraerá y estructurará el contenido automáticamente.</div>
          {processingId && (
            <div className="card" style={{ background: "rgba(255,214,0,0.10)", borderColor: "#FFD600" }} data-testid="psc-meth-processing">
              ⏳ <b>{name}</b> — La IA está adoptando esta metodología…
              <Loader2 size={12} className="spin inline ml-2"/>
            </div>
          )}
        </>
      )}

      <div>
        <label className="label">Niveles de riesgo aplicables</label>
        <div className="flex gap-2 flex-wrap">
          {["low", "medium", "high", "catastrophic"].map((r) => (
            <label key={r} className="flex items-center gap-1" style={{ fontSize: 12 }}>
              <input type="checkbox" checked={riskLevels.includes(r)} onChange={() => toggleRisk(r)} data-testid={`psc-meth-risk-${r}`}/> {r}
            </label>
          ))}
        </div>
      </div>

      {isSuper && (
        <label className="flex items-center gap-2" style={{ fontSize: 12 }}>
          <input type="checkbox" checked={isGlobal} onChange={(e) => setIsGlobal(e.target.checked)} data-testid="psc-meth-global"/>
          🌐 Metodología Global (visible para todos los clientes)
        </label>
      )}

      <div className="flex justify-end gap-2">
        <button className="btn" onClick={onClose} disabled={saving}>Cancelar</button>
        <button className="btn btn-primary" onClick={mode === "manual" ? saveManual : uploadFile} disabled={saving} data-testid="psc-meth-save">
          {saving ? <Loader2 size={12} className="spin"/> : <Plus size={12}/>} {mode === "manual" ? "Guardar Metodología" : "Subir y Procesar"}
        </button>
      </div>
    </div>
  );
}

function EditMethodForm({ m, onClose, onSaved }) {
  const [name, setName] = useState(m.name || "");
  const [acronym, setAcronym] = useState(m.acronym || "");
  const [description, setDescription] = useState(m.description || "");
  const [purpose, setPurpose] = useState(m.purpose || "");
  const [stepsText, setStepsText] = useState((m.steps || []).join("\n"));
  const [keyConceptsText, setKeyConceptsText] = useState((m.key_concepts || []).join(", "));
  const [riskLevels, setRiskLevels] = useState(m.risk_levels || []);
  const [saving, setSaving] = useState(false);

  function toggleRisk(r) { setRiskLevels(riskLevels.includes(r) ? riskLevels.filter((x) => x !== r) : [...riskLevels, r]); }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/methodologies/${m.id}`, {
        name: name.trim(), acronym, description, purpose,
        risk_levels: riskLevels,
        steps: stepsText.split("\n").map((s) => s.trim()).filter(Boolean),
        key_concepts: keyConceptsText.split(",").map((s) => s.trim()).filter(Boolean),
      });
      onSaved?.();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-2" data-testid="psc-meth-edit-form">
      <div><label className="label">Nombre</label><input className="input" value={name} onChange={(e) => setName(e.target.value)}/></div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="label">Acrónimo</label><input className="input" value={acronym} onChange={(e) => setAcronym(e.target.value)}/></div>
        <div>
          <label className="label">Niveles de riesgo</label>
          <div className="flex gap-1 flex-wrap">
            {["low", "medium", "high", "catastrophic"].map((r) => (
              <label key={r} className="flex items-center gap-1" style={{ fontSize: 11 }}>
                <input type="checkbox" checked={riskLevels.includes(r)} onChange={() => toggleRisk(r)}/> {r}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div><label className="label">Descripción</label><textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)}/></div>
      <div><label className="label">Cuándo aplicar</label><textarea className="input" rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)}/></div>
      <div><label className="label">Pasos</label><textarea className="input" rows={4} value={stepsText} onChange={(e) => setStepsText(e.target.value)}/></div>
      <div><label className="label">Conceptos clave</label><input className="input" value={keyConceptsText} onChange={(e) => setKeyConceptsText(e.target.value)}/></div>
      {m.source !== "manual" && m.raw_content && (
        <details><summary className="text-xs text-dim">Contenido extraído del documento</summary>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 11, maxHeight: 200, overflow: "auto" }}>{m.raw_content.slice(0, 4000)}</pre>
          {m.ai_extraction_notes && <div className="text-xs text-dim mt-1">Notas IA: {m.ai_extraction_notes}</div>}
        </details>
      )}
      <div className="flex justify-end gap-2">
        <button className="btn" onClick={onClose} disabled={saving}>Cancelar</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <Loader2 size={12} className="spin"/> : <CheckCircle size={12}/>} Guardar cambios</button>
      </div>
    </div>
  );
}

function TrizTab({ c, demoMode, inv, reload, busy, withBusy }) {
  const t = c.trizResult;
  return (
    <div className="space-y-3">
      <div className="card">
        <div className="card-title">TRIZ — Innovación post-RCA</div>
        {!demoMode && <button className="btn btn-primary mt-2" disabled={busy === "triz" || !c.investigation?.aiResults} onClick={() => withBusy("triz", async () => { await inv.runTRIZ(c.id); await reload(); })} data-testid="psc-triz-btn">
          {busy === "triz" ? <Loader2 size={12} className="spin"/> : <Sparkles size={12}/>} Ejecutar TRIZ
        </button>}
      </div>
      {t && (
        <div className="card">
          {t.primaryContradiction && <div><b>Contradicción principal:</b> {t.primaryContradiction}</div>}
          {(t.appliedPrinciples || []).map((p, i) => (
            <div key={i} className="mt-2"><b>P{p.principleNumber} {p.principleName}</b>
              <ul className="text-sm">{(p.solutions || []).map((s, j) => <li key={j}><b>{s.idea}</b> — {s.implementation}</li>)}</ul>
            </div>
          ))}
          {t.innovativeInsight && <div className="mt-2"><b>Insight clave:</b> {t.innovativeInsight}</div>}
        </div>
      )}
    </div>
  );
}

function PlanTab({ c, demoMode, inv, reload }) {
  const actions = c.actionPlan?.actions || [];
  if (!actions.length) return <div className="card text-dim">Sin plan de acción. Genera desde la pestaña Investigación IA.</div>;
  const stats = actions.reduce((s, a) => { s[a.status] = (s[a.status] || 0) + 1; return s; }, {});
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2">
        <div className="kpi-card"><div className="label">Pendiente</div><div className="value">{stats.Pending || 0}</div></div>
        <div className="kpi-card"><div className="label">En Proceso</div><div className="value">{stats["In Progress"] || 0}</div></div>
        <div className="kpi-card"><div className="label">Completadas</div><div className="value">{stats.Completed || 0}</div></div>
        <div className="kpi-card"><div className="label">Vencidas</div><div className="value">{stats.Overdue || 0}</div></div>
      </div>
      {actions.map((a, i) => {
        const tcolor = {Immediate:"#D50000", "Short-term":"#FF6D00", "Long-term":"#1565C0", Cultural:"#9C27B0"}[a.type] || "#1565C0";
        return (
          <div key={a.id || i} className="card" style={{ borderLeft: `4px solid ${tcolor}` }} data-testid={`psc-action-${a.id || i}`}>
            <div className="flex justify-between items-start">
              <div>
                <b>{a.id}</b> · <Badge color={tcolor}>{a.type}</Badge> · <Badge>{a.priority}</Badge>
                <div className="mt-1">{a.description}</div>
                <div className="text-xs text-dim mt-1">Resp: {a.owner || "—"} · Vence: {a.dueDate || "—"} {a.kpi ? `· KPI: ${a.kpi}` : ""}</div>
              </div>
              {!demoMode && <select className="input" value={a.status} onChange={async (e) => {
                const upd = [...actions]; upd[i] = { ...a, status: e.target.value };
                await inv.updateCase(c.id, { actionPlan: { actions: upd } }); await reload();
              }} style={{ width: 120 }}>
                {["Pending","In Progress","Completed","Overdue","Cancelled"].map((s) => <option key={s}>{s}</option>)}
              </select>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EffectivenessTab({ c, demoMode, inv, reload }) {
  const eff = c.effectiveness || {};
  const actions = c.actionPlan?.actions || [];
  const completed = actions.filter((a) => a.status === "Completed").length;
  const overdue = actions.filter((a) => a.status === "Overdue").length;
  const barriers = eff.barriers || [];
  const active = barriers.filter((b) => b.status === "Active").length;
  const pct = actions.length ? Math.round(completed * 100 / actions.length) : 0;
  const [form, setForm] = useState({ evaluatorName: eff.evaluatorName || "", evaluatorRole: eff.evaluatorRole || "", comments: eff.comments || "" });
  async function save() {
    await inv.updateCase(c.id, { effectiveness: { ...eff, ...form, evaluatedAt: new Date().toISOString() } });
    await reload();
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2">
        <div className="kpi-card"><div className="label">% Completado</div><div className="value">{pct}%</div></div>
        <div className="kpi-card"><div className="label" style={{ color: "#D50000" }}>Vencidas</div><div className="value">{overdue}</div></div>
        <div className="kpi-card"><div className="label">Barreras</div><div className="value">{barriers.length}</div></div>
        <div className="kpi-card"><div className="label">Barreras Activas</div><div className="value">{active}</div></div>
      </div>
      <div className="card">
        <div className="card-title">Evaluador</div>
        <div className="grid grid-cols-2 gap-2">
          <input className="input" placeholder="Nombre" value={form.evaluatorName} onChange={(e) => setForm({ ...form, evaluatorName: e.target.value })} data-testid="psc-eff-name"/>
          <input className="input" placeholder="Cargo" value={form.evaluatorRole} onChange={(e) => setForm({ ...form, evaluatorRole: e.target.value })}/>
        </div>
        <textarea className="input mt-2" rows={3} placeholder="Narrativa de efectividad" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })}/>
        {!demoMode && <button className="btn btn-primary mt-2" onClick={save} data-testid="psc-eff-save">Guardar</button>}
      </div>
      <div className="card">
        <div className="card-title">Barreras de Defensa</div>
        <ul className="text-sm">
          {barriers.map((b) => (
            <li key={b.id} style={{ borderLeft: "3px solid " + ({Technical:"#1565C0",Administrative:"#FF6D00",Cultural:"#9C27B0","Process Safety":"#00C853"}[b.type] || "#1565C0"), paddingLeft: 8, marginBottom: 6 }}>
              <b>{b.desc}</b> · <Badge>{b.type}</Badge> · <Badge>{b.status}</Badge> · Efectividad: <Badge>{b.effectiveness}</Badge>
            </li>
          ))}
          {!barriers.length && <li className="text-dim">Sin barreras documentadas.</li>}
        </ul>
      </div>
    </div>
  );
}

function CultureTab({ c, demoMode, inv, reload }) {
  const cu = c.culture || {};
  const [form, setForm] = useState({
    lessonsLearned: cu.lessonsLearned || "",
    trainingActions: (cu.trainingActions || []).join("\n"),
    kpis: (cu.kpis || []).join("\n"),
  });
  async function save() {
    await inv.updateCase(c.id, { culture: {
      lessonsLearned: form.lessonsLearned,
      trainingActions: form.trainingActions.split("\n").filter(Boolean),
      kpis: form.kpis.split("\n").filter(Boolean),
      updatedAt: new Date().toISOString(),
    }});
    await reload();
  }
  return (
    <div className="card space-y-2">
      <div><label className="label">Lección Aprendida</label><textarea className="input" rows={3} value={form.lessonsLearned} onChange={(e) => setForm({ ...form, lessonsLearned: e.target.value })} data-testid="psc-cul-lesson"/></div>
      <div><label className="label">Acciones de Capacitación (una por línea)</label><textarea className="input" rows={3} value={form.trainingActions} onChange={(e) => setForm({ ...form, trainingActions: e.target.value })}/></div>
      <div><label className="label">KPIs Culturales (uno por línea)</label><textarea className="input" rows={3} value={form.kpis} onChange={(e) => setForm({ ...form, kpis: e.target.value })}/></div>
      {!demoMode && <button className="btn btn-primary" onClick={save} data-testid="psc-cul-save">Guardar</button>}
    </div>
  );
}

function MsiaTab({ c, demoMode, inv, reload, busy, withBusy }) {
  const SYSTEMS = [
    { block: "Operativos de Campo", color: "#1565C0", items: ["PTW", "JSA", "SOP", "LOTO", "SIMOPS", "Plan de Emergencias"] },
    { block: "Análisis de Riesgo", color: "#FF6D00", items: ["Matriz de Riesgos", "HAZOP", "RBI", "LOPA", "HRA"] },
    { block: "Integridad de Activos", color: "#00C853", items: ["MI", "SCE", "AMS", "SIS", "ITPM"] },
    { block: "Administrativos y Personal", color: "#9C27B0", items: ["MOC", "Reporte Incidentes", "Capacitación", "Contratistas", "FRMS", "Salud Ocupacional", "BBS"] },
    { block: "Gobernanza", color: "#607D8B", items: ["Manual HSEQ", "Política HSEQ", "Código Conducta", "Plan Calidad", "Estructura Org.", "KPIs", "Auditorías"] },
  ];
  const [sel, setSel] = useState(c.msia?.selected || {});
  const comm = c.msia?.commResult || {};
  function toggle(k) { setSel({ ...sel, [k]: !sel[k] }); }
  const nSel = Object.values(sel).filter(Boolean).length;
  return (
    <div className="space-y-3">
      <div className="text-xs text-dim">MSIA — Management System Impact Assessment. {nSel} sistemas seleccionados.</div>
      {SYSTEMS.map((b) => (
        <div key={b.block} className="card" style={{ borderLeft: `3px solid ${b.color}` }}>
          <div className="card-title">{b.block}</div>
          <div className="flex gap-2 flex-wrap mt-1">
            {b.items.map((s) => (
              <label key={s} className="flex items-center gap-1 text-sm" style={{ background: sel[s] ? b.color + "22" : "transparent", padding: "2px 6px", borderRadius: 4 }}>
                <input type="checkbox" checked={!!sel[s]} onChange={() => toggle(s)} data-testid={`psc-msia-${s.replace(/[^a-zA-Z]/g, "")}`}/> {s}
              </label>
            ))}
          </div>
        </div>
      ))}
      {!demoMode && <button className="btn btn-primary" disabled={busy === "msia" || nSel === 0 || !c.investigation?.aiResults} onClick={() => withBusy("msia", async () => { await inv.runMSIA(c.id, { selected: sel, notes: {} }); await reload(); })} data-testid="psc-msia-run">
        {busy === "msia" ? <Loader2 size={12} className="spin"/> : <Sparkles size={12}/>} Generar Comunicaciones Diferenciadas
      </button>}
      {comm.operative && (
        <div className="card"><div className="card-title">👷 Operativo</div>
          {comm.operative.title && <div><b>{comm.operative.title}</b></div>}
          {comm.operative.key_message && <div className="mt-1">{comm.operative.key_message}</div>}
        </div>
      )}
      {comm.middle && (
        <div className="card"><div className="card-title">🔧 Mandos Medios</div>
          {comm.middle.title && <div><b>{comm.middle.title}</b></div>}
          {comm.middle.key_message && <div className="mt-1">{comm.middle.key_message}</div>}
        </div>
      )}
      {comm.executive && (
        <div className="card"><div className="card-title">🏛 Gerencia / Dirección</div>
          {comm.executive.headline && <div><b>{comm.executive.headline}</b></div>}
          {comm.executive.governance_action && <div className="mt-1">{comm.executive.governance_action}</div>}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Module wrapper (router entry)
// ─────────────────────────────────────────────────────────────────
export default function InvestigationsModule() {
  const { user } = useAuth();
  if (user?.role === "SUPER_ADMIN" && !user?.audit_tenant_id) {
    return (
      <SuperAdminModuleView
        moduleName="Investigaciones HSEQ"
        apiEndpoint="/investigations/superadmin/overview"
      />
    );
  }
  return <Dashboard />;
}
